import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Lightbulb, Check, HelpCircle, ChevronRight, Sparkles, X } from 'lucide-react';

// Web Audio API sound generator for playful kid-friendly sounds
const createAudioContext = () => {
  if (typeof window === 'undefined') return null;
  return new (window.AudioContext || window.webkitAudioContext)();
};

const playChime = (ctx, frequencies, durations, type = 'sine', volume = 0.25) => {
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') ctx.resume();
    let offset = 0;
    frequencies.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, ctx.currentTime + offset);
      gain.gain.setValueAtTime(volume, ctx.currentTime + offset);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + offset + durations[i]);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + offset);
      osc.stop(ctx.currentTime + offset + durations[i]);
      offset += durations[i] * 0.6;
    });
  } catch (e) { /* audio not available */ }
};

const sounds = {
  select: (ctx) => playChime(ctx, [880], [0.08], 'sine', 0.12),
  correct: (ctx) => playChime(ctx, [523, 659, 784, 1047], [0.15, 0.15, 0.15, 0.3], 'sine', 0.2),
  wrong: (ctx) => playChime(ctx, [300, 200], [0.2, 0.35], 'triangle', 0.18),
  coins: (ctx) => playChime(ctx, [1200, 1400, 1600, 1800], [0.06, 0.06, 0.06, 0.12], 'sine', 0.15),
};

const RiddleCard = ({ riddle, index, onAnswer, answered, result, onNextRiddle, isLast, soundEnabled }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [showWrongFeedback, setShowWrongFeedback] = useState(false);
  const [showRewardAnimation, setShowRewardAnimation] = useState(false);
  const [localResult, setLocalResult] = useState(null);
  const [maxedOut, setMaxedOut] = useState(false);
  const [hintText, setHintText] = useState(riddle?.hint || '');
  const audioCtx = useRef(null);

  useEffect(() => {
    audioCtx.current = createAudioContext();
    return () => {
      if (audioCtx.current && audioCtx.current.state !== 'closed') {
        audioCtx.current.close().catch(() => {});
      }
    };
  }, []);

  const play = useCallback((key) => {
    if (soundEnabled && sounds[key]) sounds[key](audioCtx.current);
  }, [soundEnabled]);

  const handleOptionSelect = (option) => {
    if (answered || isSubmitting || showRewardAnimation || maxedOut) return;
    setSelectedOption(option);
    play('select');
  };

  const handleSubmit = async () => {
    if (!selectedOption || answered || isSubmitting || maxedOut) return;
    setIsSubmitting(true);

    const submitResult = await onAnswer(riddle.id, selectedOption);

    if (submitResult) {
      if (submitResult.correct) {
        play('correct');
        setLocalResult(submitResult);
        setShowRewardAnimation(true);
        setTimeout(() => play('coins'), 500);
      } else {
        play('wrong');
        setShowWrongFeedback(true);
        setShowHint(true);
        setAttempts(submitResult.attempts || attempts + 1);
        if (submitResult.hint) setHintText(submitResult.hint);
        setSelectedOption(null);

        if (submitResult.max_attempts_reached) {
          setMaxedOut(true);
          setShowWrongFeedback(false);
        } else {
          setTimeout(() => setShowWrongFeedback(false), 2500);
        }
      }
    }
    setIsSubmitting(false);
  };

  const handleNextRiddle = () => {
    play('select');
    onNextRiddle();
  };

  const categoryColors = {
    logic: { bg: 'from-blue-500 to-indigo-600', light: 'bg-blue-50', border: 'border-blue-200', accent: 'text-blue-600' },
    observation: { bg: 'from-green-500 to-emerald-600', light: 'bg-green-50', border: 'border-green-200', accent: 'text-green-600' },
    pattern: { bg: 'from-purple-500 to-pink-600', light: 'bg-purple-50', border: 'border-purple-200', accent: 'text-purple-600' },
    everyday: { bg: 'from-orange-500 to-amber-600', light: 'bg-orange-50', border: 'border-orange-200', accent: 'text-orange-600' }
  };

  const categoryIcons = { logic: '🧩', observation: '👀', pattern: '🔄', everyday: '🏠' };
  const colors = categoryColors[riddle?.category] || categoryColors.logic;

  // ──── Reward Animation Screen ────
  if (showRewardAnimation && localResult) {
    return (
      <div data-testid="reward-screen" className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-yellow-100 via-amber-50 to-orange-100 border-4 border-yellow-400 shadow-2xl animate-pop">
        {/* Confetti */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(24)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 0.5}s`,
                backgroundColor: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FF9FF3'][i % 6],
                width: `${8 + Math.random() * 6}px`,
                height: `${8 + Math.random() * 6}px`,
                borderRadius: Math.random() > 0.5 ? '50%' : '2px',
              }}
            />
          ))}
        </div>

        <div className="p-8 text-center relative z-10">
          <div className="flex justify-center gap-2 mb-3">
            {[...Array(3)].map((_, i) => (
              <span key={i} className="text-5xl animate-bounce" style={{ animationDelay: `${i * 0.12}s` }}>
                ⭐
              </span>
            ))}
          </div>

          <h3 data-testid="reward-title" className="text-3xl font-black text-green-600 mb-1 font-fredoka">
            Correct!
          </h3>
          <p className="text-slate-500 mb-4 text-sm">You nailed it!</p>

          <div className="my-5">
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-yellow-400 to-amber-500 px-8 py-4 rounded-2xl shadow-lg animate-bounce-soft">
              <span className="text-4xl">🪙</span>
              <span data-testid="reward-coins" className="text-4xl font-black text-white">+{localResult.coins_earned}</span>
              <span className="text-xl text-white/90">coins!</span>
            </div>
          </div>

          {riddle.fun_fact && (
            <div className="bg-white/80 rounded-2xl p-4 mb-5 border-2 border-yellow-300 text-left">
              <p className="text-slate-700 text-sm">
                <Lightbulb className="w-4 h-4 inline text-yellow-500 mr-1" />
                <span className="font-bold">Fun fact:</span> {riddle.fun_fact}
              </p>
            </div>
          )}

          <button
            data-testid="next-riddle-btn"
            onClick={handleNextRiddle}
            className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl font-bold text-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all flex items-center gap-2 mx-auto"
          >
            {isLast ? (
              <><Sparkles className="w-6 h-6" /> See Results!</>
            ) : (
              <>Next Riddle <ChevronRight className="w-6 h-6" /></>
            )}
          </button>
        </div>

        {/* Corner decorations */}
        <div className="absolute top-3 left-3 text-3xl animate-spin-slow">🌟</div>
        <div className="absolute top-3 right-3 text-3xl animate-spin-slow" style={{ animationDelay: '0.5s' }}>✨</div>
        <div className="absolute bottom-3 left-3 text-3xl animate-bounce">🎊</div>
        <div className="absolute bottom-3 right-3 text-3xl animate-bounce" style={{ animationDelay: '0.3s' }}>🎈</div>
      </div>
    );
  }

  // ──── Max attempts reached (failed) screen ────
  if (maxedOut && !answered) {
    return (
      <div data-testid="max-attempts-screen" className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-50 to-slate-100 border-3 border-slate-300 shadow-lg animate-pop">
        <div className={`bg-gradient-to-r ${colors.bg} p-4 text-white flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <span className="text-2xl">{categoryIcons[riddle?.category] || '🧩'}</span>
            <span className="font-bold capitalize">{riddle?.category} Riddle #{index + 1}</span>
          </div>
          <span className="bg-white/20 px-3 py-1 rounded-full text-sm">3/3 attempts</span>
        </div>
        <div className="p-6 text-center">
          <div className="text-5xl mb-3">🤷</div>
          <h3 className="text-xl font-bold text-slate-800 mb-2 font-fredoka">No worries!</h3>
          <p className="text-slate-600 mb-4 text-sm">The answer was:</p>
          <div className="bg-green-50 border-2 border-green-300 rounded-2xl px-6 py-3 inline-block mb-5">
            <span data-testid="correct-answer-reveal" className="text-lg font-bold text-green-700">{result?.correct_answer}</span>
          </div>
          <br />
          <button
            data-testid="next-riddle-btn-failed"
            onClick={handleNextRiddle}
            className="px-8 py-4 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all inline-flex items-center gap-2"
          >
            {isLast ? (
              <><Sparkles className="w-5 h-5" /> See Results!</>
            ) : (
              <>Next Riddle <ChevronRight className="w-5 h-5" /></>
            )}
          </button>
        </div>
      </div>
    );
  }

  // ──── Already answered (returning user) ────
  if (answered && result) {
    return (
      <div className="rounded-3xl bg-white border-3 border-green-400 shadow-lg overflow-hidden">
        <div className={`bg-gradient-to-r ${colors.bg} p-4 text-white flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <span className="text-2xl">{categoryIcons[riddle?.category] || '🧩'}</span>
            <span className="font-bold capitalize">{riddle?.category} Riddle #{index + 1}</span>
          </div>
          {result.correct ? (
            <span className="bg-green-400/30 px-3 py-1 rounded-full text-sm font-bold">Correct!</span>
          ) : (
            <span className="bg-red-400/30 px-3 py-1 rounded-full text-sm font-bold">Missed</span>
          )}
        </div>
        <div className="p-5 text-center">
          <div className="text-4xl mb-2">{result.correct ? '🎉' : '💪'}</div>
          <p className="text-slate-600 text-sm mb-3">
            {result.correct ? `You earned ${result.coins_earned} coins!` : `The answer was: ${result.correct_answer}`}
          </p>
          <button
            data-testid="next-riddle-btn-answered"
            onClick={handleNextRiddle}
            className="px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl font-bold shadow-md hover:shadow-lg transform hover:scale-105 transition-all inline-flex items-center gap-2"
          >
            {isLast ? (
              <><Sparkles className="w-5 h-5" /> See Results!</>
            ) : (
              <>Next Riddle <ChevronRight className="w-5 h-5" /></>
            )}
          </button>
        </div>
      </div>
    );
  }

  // ──── Active Riddle Panel Card ────
  return (
    <div data-testid={`riddle-card-${index}`} className={`
      relative overflow-hidden rounded-3xl bg-white
      border-3 ${colors.border}
      shadow-xl transition-all duration-300
    `}>
      {/* Header */}
      <div className={`bg-gradient-to-r ${colors.bg} p-5 text-white`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{categoryIcons[riddle?.category] || '🧩'}</span>
            <div>
              <span className="font-bold text-lg capitalize block">{riddle?.category} Riddle</span>
              <span className="text-white/80 text-sm">Think carefully!</span>
            </div>
          </div>
          <div className="bg-white/20 px-4 py-2 rounded-full text-lg font-bold">
            #{index + 1}
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Visual clue */}
        {riddle?.visual_clue && (
          <div className={`mb-4 p-3 ${colors.light} rounded-xl border ${colors.border} flex items-center gap-2 text-sm`}>
            <span className="text-xl">🖼️</span>
            <span className="text-slate-700">{riddle.visual_clue}</span>
          </div>
        )}

        {/* Riddle text panel */}
        <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-5 mb-5 border-2 border-slate-200">
          <p data-testid="riddle-text" className="text-xl font-semibold text-slate-800 leading-relaxed text-center">
            "{riddle?.riddle}"
          </p>
        </div>

        {/* Wrong answer feedback */}
        {showWrongFeedback && (
          <div data-testid="wrong-feedback" className="mb-4 p-4 bg-red-50 rounded-2xl border-2 border-red-200 animate-shake flex items-center gap-3">
            <span className="text-3xl">🤔</span>
            <div>
              <p className="font-bold text-red-700">Not quite!</p>
              <p className="text-red-600 text-sm">Try again — you've got this! ({3 - attempts} {3 - attempts === 1 ? 'try' : 'tries'} left)</p>
            </div>
          </div>
        )}

        {/* Hint */}
        {!answered && (
          <button
            data-testid="hint-toggle"
            onClick={() => { setShowHint(!showHint); if (!showHint) play('select'); }}
            className={`mb-4 flex items-center gap-2 px-4 py-2 rounded-xl transition-all text-sm ${
              showHint ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600 hover:bg-purple-50'
            }`}
          >
            <HelpCircle className="w-4 h-4" />
            <span className="font-medium">{showHint ? 'Hint shown below' : 'Need a hint?'}</span>
            {attempts > 0 && <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">Attempt {attempts + 1}/3</span>}
          </button>
        )}

        {showHint && !answered && hintText && (
          <div data-testid="hint-content" className="mb-5 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl border-2 border-purple-200 flex items-center gap-3 animate-pop">
            <Lightbulb className="w-7 h-7 text-purple-500 flex-shrink-0" />
            <div>
              <p className="font-bold text-purple-700 mb-0.5 text-sm">Hint:</p>
              <p className="text-purple-600 text-sm">{hintText}</p>
            </div>
          </div>
        )}

        {/* Options */}
        <div className="grid grid-cols-1 gap-3 mb-5">
          {riddle?.options?.map((option, idx) => {
            const isSelected = selectedOption === option;
            const optionLetter = String.fromCharCode(65 + idx);
            return (
              <button
                key={idx}
                data-testid={`option-${idx}`}
                onClick={() => handleOptionSelect(option)}
                disabled={answered || isSubmitting || maxedOut}
                className={`
                  p-4 rounded-2xl text-left font-medium transition-all duration-200 transform
                  ${isSelected
                    ? 'bg-gradient-to-r from-primary/30 to-amber-200 border-3 border-primary scale-[1.02] shadow-md'
                    : 'bg-slate-50 border-2 border-slate-200 hover:border-purple-300 hover:bg-purple-50 hover:scale-[1.01]'
                  }
                `}
              >
                <div className="flex items-center gap-4">
                  <span className={`
                    w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold transition-all
                    ${isSelected ? 'bg-primary text-black shadow-md' : 'bg-slate-200 text-slate-600'}
                  `}>
                    {optionLetter}
                  </span>
                  <span className="text-base text-slate-800">{option}</span>
                  {isSelected && <Check className="w-5 h-5 text-primary ml-auto" />}
                </div>
              </button>
            );
          })}
        </div>

        {/* Submit */}
        {!answered && !maxedOut && (
          <button
            data-testid="submit-answer-btn"
            onClick={handleSubmit}
            disabled={!selectedOption || isSubmitting}
            className={`
              w-full py-4 rounded-2xl font-bold text-xl transition-all transform
              ${selectedOption
                ? 'bg-gradient-to-r from-primary to-amber-400 text-black hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }
            `}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                Checking...
              </span>
            ) : (
              `Submit Answer 🎯`
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default RiddleCard;
